import api from "./api.service";
import { getSelectedColoc } from "./coloc.service";

/** Énergie d'un fournisseur, lue par les graphiques de consommation (#15). */
export type Energie = 'gaz' | 'electricite' | 'eau' | 'autre';

export const LIBELLES_ENERGIE: Record<Energie, string> = {
  gaz: 'Gaz',
  electricite: 'Électricité',
  eau: 'Eau',
  autre: 'Autre (hors graphiques d\'énergie)',
};

export interface Provider {
  id: string;
  providerName: string;
  colocId?: string;
  userId?: string;
  amountOfInvoice?: number;
  prcOfInvoice?: number;
  expenseCategoryId?: string;
  requiresConsumptionPeriod?: boolean;
  energy?: Energie | null;
  createdAt?: string;
  updatedAt?: string;
  expenseCategory?: {
    id: string;
    name: string;
  };
}

export interface ProviderSuggestion {
  providerName: string;
  expenseCategory: string;
  requiresConsumptionPeriod: boolean;
  /** `null` : fournisseur de plusieurs énergies, à choisir. */
  energy: Energie | null;
}

export interface ProviderColocAssignment {
  colocId: string;
  colocName: string;
  prcOfInvoice?: number | null;
  amountOfInvoice?: number | null;
}

export interface ProviderWithColocs extends Provider {
  colocs: ProviderColocAssignment[];
}

class ProvidersService {
  async getProviders(): Promise<Provider[]> {
    const selectedColoc = getSelectedColoc();
    if (!selectedColoc) {
      throw new Error("No coloc selected");
    }
    const response = await api.get(`/providers?colocId=${selectedColoc.id}`);
    return response.data;
  }

  async getProviderById(id: string): Promise<Provider> {
    const response = await api.get(`/providers/${id}`);
    return response.data;
  }

  async getProviderForColoc(id: string, colocId: string): Promise<Provider | undefined> {
    const response = await api.get(`/providers?colocId=${colocId}`);
    const providers: Provider[] = response.data;
    return providers.find(p => p.id === id);
  }

  async createProvider(providerData: Partial<Provider>): Promise<Provider> {
    const response = await api.post("/providers", providerData);
    return response.data;
  }

  async updateProvider(id: string, providerData: Partial<Provider>): Promise<Provider> {
    const response = await api.put(`/providers/${id}`, providerData);
    return response.data;
  }

  async deleteProvider(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/providers/${id}`);
    return response.data;
  }

  async getProvidersByColocId(colocId: string): Promise<Provider[]> {
    const response = await api.get(`/providers?colocId=${colocId}`);
    return response.data;
  }

  async getSuggestions(): Promise<ProviderSuggestion[]> {
    const response = await api.get("/providers/suggestions");
    return response.data;
  }

  async getAllProviders(): Promise<ProviderWithColocs[]> {
    const response = await api.get("/providers");
    return response.data;
  }

  async assignToColoc(
    providerId: string,
    colocId: string,
    prcOfInvoice?: number | null,
    amountOfInvoice?: number | null
  ): Promise<any> {
    const response = await api.post(`/providers/${providerId}/colocs`, {
      colocId,
      prcOfInvoice: prcOfInvoice || null,
      amountOfInvoice: amountOfInvoice || null,
    });
    return response.data;
  }

  async unassignFromColoc(providerId: string, colocId: string): Promise<any> {
    const response = await api.delete(`/providers/${providerId}/colocs/${colocId}`);
    return response.data;
  }

  async updateColocSettings(
    providerId: string,
    colocId: string,
    prcOfInvoice?: number | null,
    amountOfInvoice?: number | null
  ): Promise<any> {
    const response = await api.put(`/providers/${providerId}/colocs/${colocId}`, {
      prcOfInvoice: prcOfInvoice || null,
      amountOfInvoice: amountOfInvoice || null,
    });
    return response.data;
  }
}

export const providersService = new ProvidersService();
